import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, CheckCircle, AlertCircle, Loader2, ChevronDown, Menu } from 'lucide-react';
import api from '../../services/api';
import { Logo } from '../common';
import Footer from '../common/Footer';
import SignUpModal from '../auth/SignUpModal';
import DemoRegistrationModal from './DemoRegistrationModal';
import StackSelector from '../stack/StackSelector';
import StackPanel from '../stack/StackPanel';
import useMetaTags from '../../hooks/useMetaTags';
import useStackNavigation from '../../hooks/useStackNavigation';
import { useStackFlow } from '../../context/StackFlowContext';

// ─── Country picker data ───
const COUNTRY_CODES = [
  { code: 'US', flag: '\u{1F1FA}\u{1F1F8}', dial: '+1', name: 'United States' },
  { code: 'GB', flag: '\u{1F1EC}\u{1F1E7}', dial: '+44', name: 'United Kingdom' },
  { code: 'KE', flag: '\u{1F1F0}\u{1F1EA}', dial: '+254', name: 'Kenya' },
  { code: 'UG', flag: '\u{1F1FA}\u{1F1EC}', dial: '+256', name: 'Uganda' },
  { code: 'TZ', flag: '\u{1F1F9}\u{1F1FF}', dial: '+255', name: 'Tanzania' },
  { code: 'NG', flag: '\u{1F1F3}\u{1F1EC}', dial: '+234', name: 'Nigeria' },
  { code: 'ZA', flag: '\u{1F1FF}\u{1F1E6}', dial: '+27', name: 'South Africa' },
  { code: 'RW', flag: '\u{1F1F7}\u{1F1FC}', dial: '+250', name: 'Rwanda' },
  { code: 'GH', flag: '\u{1F1EC}\u{1F1ED}', dial: '+233', name: 'Ghana' },
  { code: 'IN', flag: '\u{1F1EE}\u{1F1F3}', dial: '+91', name: 'India' },
  { code: 'AE', flag: '\u{1F1E6}\u{1F1EA}', dial: '+971', name: 'UAE' },
  { code: 'SG', flag: '\u{1F1F8}\u{1F1EC}', dial: '+65', name: 'Singapore' },
  { code: 'CH', flag: '\u{1F1E8}\u{1F1ED}', dial: '+41', name: 'Switzerland' },
  { code: 'JP', flag: '\u{1F1EF}\u{1F1F5}', dial: '+81', name: 'Japan' },
  { code: 'DE', flag: '\u{1F1E9}\u{1F1EA}', dial: '+49', name: 'Germany' },
];

// ─── Waitlist modal (3 steps: email → phone → thank you) ───
const WaitlistModal = ({ onClose, context }) => {
  const [step, setStep] = useState(1);
  const [subscriberId, setSubscriberId] = useState(null);

  // Step 1 state
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailMessage, setEmailMessage] = useState('');

  // Step 2 state
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(COUNTRY_CODES[0]);
  const [whatsappChecked, setWhatsappChecked] = useState(false);
  const [telegramChecked, setTelegramChecked] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState('idle');
  const [phoneMessage, setPhoneMessage] = useState('');

  const validateEmail = (em) =>
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(em);

  const storeLocally = (em) => {
    try {
      const existing = JSON.parse(localStorage.getItem('flowWaitlist') || '[]');
      if (!existing.includes(em.toLowerCase())) {
        existing.push(em.toLowerCase());
        localStorage.setItem('flowWaitlist', JSON.stringify(existing));
      }
      return true;
    } catch { return false; }
  };

  // Step 1: Submit email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setEmailStatus('error'); setEmailMessage('Please enter your email address.'); return; }
    if (!validateEmail(email)) { setEmailStatus('error'); setEmailMessage('Please enter a valid email address.'); return; }

    setEmailStatus('loading');
    setEmailMessage('');

    try {
      const params = new URLSearchParams(window.location.search);
      const result = await api.submitWaitlistSubscription({
        email: email.toLowerCase().trim(),
        source: `waitlist_${context}`,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
      });

      storeLocally(email);
      setSubscriberId(result.subscriberId);
      setStep(2);
    } catch (err) {
      if (storeLocally(email)) {
        setSubscriberId('local-' + Date.now());
        setStep(2);
        return;
      }
      setEmailStatus('error');
      setEmailMessage('Something went wrong. Please try again.');
    }
  };

  // Step 2: Submit phone (or skip)
  const handlePhoneSubmit = async () => {
    const hasPhone = phone.trim().length > 0;
    const hasChannel = whatsappChecked || telegramChecked;

    if (hasPhone && hasChannel && subscriberId) {
      setPhoneStatus('loading');
      setPhoneMessage('');
      try {
        const channels = [];
        if (whatsappChecked) channels.push('whatsapp');
        if (telegramChecked) channels.push('telegram');

        await api.updateWaitlistPhone(subscriberId, {
          phone_e164: countryCode.dial + phone.replace(/\D/g, ''),
          channels,
          country_code: countryCode.code,
        });
      } catch (err) {
        // non-blocking
      }
    }

    setStep(3);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white border border-zinc-300 p-5 sm:p-8 max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-black transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* ──── Step 1: Email ──── */}
        {step === 1 && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Join the Waitlist</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              {context === 'demo'
                ? 'Get early access when we launch in your jurisdiction. We will notify you.'
                : 'Be among the first to run your cross-border cashflows on Forwardsflow.'}
            </p>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 border text-black placeholder:text-zinc-400 text-xs font-mono bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors ${
                    emailStatus === 'error' ? 'border-red-500' : 'border-zinc-300'
                  }`}
                  disabled={emailStatus === 'loading'}
                />
                {emailStatus === 'error' && (
                  <p className="mt-2 text-xs font-mono text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />{emailMessage}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={emailStatus === 'loading'}
                className="w-full py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {emailStatus === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join Waitlist'}
              </button>
            </form>
          </>
        )}

        {/* ──── Step 2: Phone + Channels ──── */}
        {step === 2 && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-1">Notify me by mobile chat too</h3>
            <p className="text-sm text-zinc-500 font-sans mb-6">(optional)</p>

            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryPicker(!showCountryPicker)}
                      className="flex items-center gap-1 px-3 py-3 border border-zinc-300 text-sm bg-white hover:border-[#4D4D4D] transition-colors min-w-[90px]"
                    >
                      <span>{countryCode.flag}</span>
                      <span className="font-mono text-xs text-zinc-600">{countryCode.dial}</span>
                      <ChevronDown className="w-3 h-3 text-zinc-400" />
                    </button>
                    {showCountryPicker && (
                      <div className="absolute z-10 top-full left-0 mt-1 bg-white border border-zinc-300 shadow-lg max-h-48 overflow-y-auto w-full sm:w-56">
                        {COUNTRY_CODES.map(c => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => { setCountryCode(c); setShowCountryPicker(false); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-left text-xs font-mono hover:bg-zinc-50 transition-colors"
                          >
                            <span>{c.flag}</span>
                            <span className="text-zinc-800">{c.name}</span>
                            <span className="text-zinc-400 ml-auto">{c.dial}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="712 345 678"
                    className="flex-1 px-4 py-3 border border-zinc-300 text-black placeholder:text-zinc-400 text-xs font-mono bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block">
                  Notification Channels
                </label>
                <label className="flex items-center gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={whatsappChecked}
                    onChange={e => setWhatsappChecked(e.target.checked)}
                    className="w-4 h-4 accent-[#25D366]"
                  />
                  <span className="text-sm font-sans" style={{ color: '#25D366' }}>WhatsApp</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={telegramChecked}
                    onChange={e => setTelegramChecked(e.target.checked)}
                    className="w-4 h-4 accent-[#0088cc]"
                  />
                  <span className="text-sm font-sans" style={{ color: '#0088cc' }}>Telegram</span>
                </label>
              </div>

              {phoneStatus === 'error' && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />{phoneMessage}
                </p>
              )}

              <button
                onClick={handlePhoneSubmit}
                disabled={phoneStatus === 'loading'}
                className="w-full py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {phoneStatus === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Next'}
              </button>
            </div>
          </>
        )}

        {/* ──── Step 3: Thank You ──── */}
        {step === 3 && (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="font-sans text-xl font-bold text-black mb-2">Thank you!</h3>
            <p className="text-sm text-zinc-600 font-sans">
              You're on the waitlist. We'll reach out when it's your turn.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 font-mono text-xs uppercase tracking-[0.15em] border border-zinc-300 text-black hover:border-[#4D4D4D] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Animated border: double-headed arrow traces the perimeter in 3 s ───
const AnimatedBorder = ({ onComplete, pill = false }) => {
  const measureRef = useRef(null);
  const [dims, setDims] = useState(null);

  useEffect(() => {
    const box = measureRef.current?.parentElement;
    if (box) {
      const { width, height } = box.getBoundingClientRect();
      setDims({ w: Math.round(width), h: Math.round(height) });
    }
  }, []);

  useEffect(() => {
    if (!dims) return;
    const t = setTimeout(onComplete, 3250);
    return () => clearTimeout(t);
  }, [dims, onComplete]);

  const { w = 0, h = 0 } = dims || {};
  const r = pill ? (h - 1) / 2 : 0;
  const perim = pill
    ? 2 * Math.max(0, w - h) + Math.PI * (h - 1)
    : 2 * (w - 1) + 2 * (h - 1);

  const motionPath = pill
    ? `M ${0.5 + r},0.5 L ${w - 0.5 - r},0.5 A ${r},${r} 0 0,1 ${w - 0.5},${0.5 + r} A ${r},${r} 0 0,1 ${w - 0.5 - r},${h - 0.5} L ${0.5 + r},${h - 0.5} A ${r},${r} 0 0,1 0.5,${h - 0.5 - r} A ${r},${r} 0 0,1 ${0.5 + r},0.5 Z`
    : `M 0.5,0.5 L ${w - 0.5},0.5 L ${w - 0.5},${h - 0.5} L 0.5,${h - 0.5} Z`;

  const rearPts = pill ? '-5,-2.5 -1,0 -5,2.5' : '-8,-4 -2,0 -8,4';
  const frontPts = pill ? '-1,-2.5 3,0 -1,2.5' : '-2,-4 4,0 -2,4';

  return (
    <div
      ref={measureRef}
      className="absolute pointer-events-none"
      style={{ top: '-1px', left: '-1px', right: '-1px', bottom: '-1px' }}
    >
      {dims && (
        <svg width={w} height={h} style={{ overflow: 'visible' }}>
          <rect
            x="0.5" y="0.5" width={w - 1} height={h - 1}
            rx={r} ry={r}
            fill="none" stroke="#d4d4d8" strokeWidth="1"
            strokeDasharray={perim} strokeDashoffset={perim}
          >
            <animate attributeName="stroke-dashoffset" from={perim} to="0" dur="3s" fill="freeze" />
          </rect>
          <g fill="#3832e3">
            <animateMotion dur="3s" fill="freeze" path={motionPath} rotate="auto" />
            <animate attributeName="opacity" from="1" to="0" begin="3s" dur="0.2s" fill="freeze" />
            <polygon points={rearPts} />
            <polygon points={frontPts} />
          </g>
        </svg>
      )}
    </div>
  );
};

// ─── Animated horizontal line: arrow draws left→right in 3 s ───
const AnimatedLine = ({ onComplete }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(null);

  useEffect(() => {
    const el = ref.current?.parentElement;
    if (el) setWidth(Math.round(el.getBoundingClientRect().width));
  }, []);

  useEffect(() => {
    if (!width) return;
    const t = setTimeout(onComplete, 3250);
    return () => clearTimeout(t);
  }, [width, onComplete]);

  return (
    <div ref={ref} className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: '1px' }}>
      {width && (
        <svg width={width} height="1" style={{ overflow: 'visible' }}>
          <line x1="0" y1="0.5" x2={width} y2="0.5"
            stroke="#e4e4e7" strokeWidth="1"
            strokeDasharray={width} strokeDashoffset={width}>
            <animate attributeName="stroke-dashoffset" from={width} to="0" dur="3s" fill="freeze" />
          </line>
          <g fill="#3832e3">
            <animateMotion dur="3s" fill="freeze" path={`M 0,0.5 L ${width},0.5`} rotate="auto" />
            <animate attributeName="opacity" from="1" to="0" begin="3s" dur="0.2s" fill="freeze" />
            <polygon points="-5,-2.5 -1,0 -5,2.5" />
            <polygon points="-1,-2.5 3,0 -1,2.5" />
          </g>
        </svg>
      )}
    </div>
  );
};

// ─── Landing page ───
const LandingPage = () => {
  const navigate = useNavigate();
  const { selected, select } = useStackNavigation();
  const { completed, setCurrentPillar } = useStackFlow();

  // Sync current pillar with stack navigation
  useEffect(() => {
    setCurrentPillar(selected);
  }, [selected, setCurrentPillar]);

  useMetaTags({
    title: 'ForwardsFlow | Full stack cashflow management.\u2122',
    description: 'Seven standalone services — yield discovery, exchange rates, compliance, hedging, liquidity, execution, portfolio — usable alone or as one cross-border cashflow.',
    canonical: 'https://www.forwardsflow.com/',
  });

  // UI state
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [waitlistContext, setWaitlistContext] = useState('apply');
  const [showDemoRegistration, setShowDemoRegistration] = useState(false);
  const [demoParams, setDemoParams] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [borderDrawn, setBorderDrawn] = useState(false);
  const handleBorderComplete = useCallback(() => setBorderDrawn(true), []);

  const openWaitlist = useCallback((ctx) => {
    setWaitlistContext(ctx || 'apply');
    setShowWaitlist(true);
  }, []);

  const openDemoRegistration = useCallback((params) => {
    setDemoParams(params);
    setShowDemoRegistration(true);
  }, []);

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b ${borderDrawn ? 'border-zinc-200' : 'border-transparent'}`} aria-label="Main navigation" role="navigation">
        {!borderDrawn && <AnimatedLine onComplete={handleBorderComplete} />}
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => setShowSignUp(true)} className="font-mono text-xs uppercase tracking-[0.15em] text-white bg-[#4D4D4D] hover:bg-black px-3 py-1.5 transition-colors">
                Sign Up
              </button>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-bold border border-zinc-300 hover:border-[#4D4D4D] px-3 py-1.5 transition-all">
                Sign In
              </Link>
              <Link to="/about" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black border border-[#3832e3] px-3 py-1.5 transition-all">
                About
              </Link>
            </div>
            <button
              className="md:hidden p-2 text-zinc-600 hover:text-black transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-200 px-6 py-4 space-y-3">
            <button
              onClick={() => { setShowSignUp(true); setMobileMenuOpen(false); }}
              className="block w-full text-left font-mono text-xs uppercase tracking-[0.15em] text-white bg-[#4D4D4D] hover:bg-black px-3 py-2 transition-colors"
            >
              Sign Up
            </button>
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-bold border border-zinc-300 hover:border-[#4D4D4D] px-3 py-2 transition-all"
            >
              Sign In
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black border border-[#3832e3] px-3 py-2 transition-all"
            >
              About
            </Link>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main role="main">

        {/* Hero + Stack */}
        <section className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto w-full">

            {/* Capsule */}
            <div className={`relative flex items-center justify-center gap-1.5 px-2 py-1 rounded-full font-sans text-[8px] sm:text-xs md:text-sm tracking-normal text-zinc-600 mb-12 animate-fade-in w-fit mx-auto border ${borderDrawn ? 'border-zinc-300' : 'border-transparent'}`}>
              {!borderDrawn && <AnimatedBorder pill onComplete={handleBorderComplete} />}
              <span className="w-2 h-2 bg-[#4D4D4D] rounded-full animate-pulse flex-shrink-0"></span>
              <span className="text-center">Full stack cashflow management.<sup className="text-[6px] sm:text-[8px]">™</sup></span>
            </div>

            {/* ─── Stack Selector (replaces PillarStrip hero) ─── */}
            <div className="max-w-6xl mx-auto mb-6">
              <StackSelector selected={selected} onSelect={select} completedSteps={completed} />
            </div>

            {/* ─── Active Panel ─── */}
            <div className="max-w-6xl mx-auto">
              <StackPanel
                selected={selected}
                openSignUp={() => setShowSignUp(true)}
                openWaitlist={openWaitlist}
                openDemoRegistration={openDemoRegistration}
                onNavigate={select}
              />
            </div>

            <p className="text-center font-mono text-xs sm:text-sm text-zinc-500 mt-12 tracking-wide">
              Your AI facilitated virtual treasury department.<sup className="text-[6px] sm:text-[8px]">™</sup>
            </p>
          </div>
        </section>

      </main>

      <Footer hideCta />

      {/* Waitlist modal */}
      {showWaitlist && (
        <WaitlistModal onClose={() => { setShowWaitlist(false); navigate('/'); }} context={waitlistContext} />
      )}

      {/* Sign Up modal */}
      {showSignUp && (
        <SignUpModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} />
      )}

      {/* Demo Registration modal */}
      {showDemoRegistration && (
        <DemoRegistrationModal
          onClose={() => setShowDemoRegistration(false)}
          demoParams={demoParams || { source: 'USD', sink: 'Kenya', instrument: 'T-Bills', tenor: '91-day T-bill', mode: 'full', hedge: 'yes', amount: 10000 }}
        />
      )}
    </div>
  );
};

export default LandingPage;
